import React, { useEffect, useState } from 'react';
import RTSpinner from '../common/RTSpinner';
import RTPageLayout from '../common/RTPageLayout';
import { apiGet } from '../../api/apiClient';
import { toastError } from '../common/toast';
import { formatDateToDDMMYYYY } from '../../utils/dateHelper';
import { formatINR, prettyStatus, statusBadgeClass } from './pgFormat';
import './PGPortal.css';

const PERIOD_OPTIONS = [
  {
    value: 'last_3_months',
    label: 'Last 3 months',
    title: 'Payments made in last 3 months',
  },
  {
    value: 'last_6_months',
    label: 'Last 6 months',
    title: 'Payments made in last 6 months',
  },
  {
    value: 'last_1_year',
    label: 'Last 1 year',
    title: 'Payments made in last 1 year',
  },
  {
    value: 'before_1_year',
    label: 'Before 1 year',
    title: 'Payments made before 1 year',
  },
];

const PGPayments = () => {
  const [period, setPeriod] = useState('last_3_months');
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await apiGet(
          `/pg/me/payments?period=${encodeURIComponent(period)}`,
        );
        setPayments(res.data || []);
      } catch (err) {
        toastError(err.response?.data?.detail || 'Unable to load payments.');
        setPayments([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [period]);

  const selectedPeriod =
    PERIOD_OPTIONS.find((opt) => opt.value === period) || PERIOD_OPTIONS[0];

  return (
    <RTPageLayout
      title="Payments"
      subtitle="Your payment history"
      mainSection
    >
      <div className="pg-portal">
        <div className="pg-payments-toolbar">
          <div className="pg-form-group pg-payments-period">
            <label htmlFor="pg-payments-period">Period</label>
            <select
              id="pg-payments-period"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              disabled={loading}
            >
              {PERIOD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <h3 className="pg-payments-count-title">
          {selectedPeriod.title}
          {!loading ? ` (${payments.length})` : ''}
        </h3>

        {loading ? (
          <RTSpinner message="Loading payments…" fullScreen={false} />
        ) : payments.length === 0 ? (
          <div className="pg-empty">No payments found for this period.</div>
        ) : (
          payments.map((p) => (
            <div key={p.payment_id} className="pg-card">
              <div className="pg-card-title">
                <h3>{formatINR(p.amount)}</h3>
                <span className={`pg-badge ${statusBadgeClass(p.status)}`}>
                  {prettyStatus(p.status)}
                </span>
              </div>
              <div className="pg-row">
                <span className="pg-row-label">Date</span>
                <span className="pg-row-value">
                  {formatDateToDDMMYYYY(p.payment_date)}
                </span>
              </div>
              <div className="pg-row">
                <span className="pg-row-label">Method</span>
                <span className="pg-row-value">
                  {p.payment_method || 'N/A'}
                </span>
              </div>
              <div className="pg-row">
                <span className="pg-row-label">Type</span>
                <span className="pg-row-value">{p.payment_type || 'N/A'}</span>
              </div>
              {p.receipt_number ? (
                <div className="pg-row">
                  <span className="pg-row-label">Receipt</span>
                  <span className="pg-row-value">{p.receipt_number}</span>
                </div>
              ) : null}
              {p.reference_number ? (
                <div className="pg-row">
                  <span className="pg-row-label">Reference</span>
                  <span className="pg-row-value">{p.reference_number}</span>
                </div>
              ) : null}
            </div>
          ))
        )}
      </div>
    </RTPageLayout>
  );
};

export default PGPayments;
